package com.havn.app.features.attachments.presentation

import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.media.HavnPickedFile
import com.havn.app.core.media.decodeHavnDataUrlBytes
import com.havn.app.core.media.havnDataUrlContentType
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.core.platform.HavnEmailAttachment
import com.havn.app.core.presentation.havnFriendlyMessage
import com.havn.app.features.attachments.domain.AgentStorageUsage
import com.havn.app.features.attachments.domain.AttachmentRepository
import com.havn.app.features.attachments.domain.AttachmentUploadContext
import com.havn.app.features.attachments.domain.HavnAttachmentSharePhoto
import com.havn.app.features.attachments.domain.HavnAttachment
import com.havn.app.features.attachments.domain.HavnAttachmentRecipient
import com.havn.app.features.attachments.domain.HavnAttachmentShareLinkTarget
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class AttachmentsViewModel(
    private val attachmentRepository: AttachmentRepository,
    private val authRepository: AuthRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Main)
) {
    private val _uiState = MutableStateFlow(AttachmentsUiState())
    val uiState: StateFlow<AttachmentsUiState> = _uiState.asStateFlow()
    private var agentId: String? = null

    init {
        scope.launch {
            authRepository.authState.collect { state ->
                agentId = (state as? AuthState.LoggedIn)?.user?.id
                if (agentId == null) {
                    _uiState.value = AttachmentsUiState()
                } else {
                    _uiState.update { it.copy(currentAgentId = agentId) }
                    refreshUsage()
                    refreshRecents()
                }
            }
        }
    }

    fun loadForProperty(propertyId: String) {
        load(propertyId = propertyId, taskId = null)
    }

    fun loadForTask(taskId: String) {
        load(propertyId = null, taskId = taskId)
    }

    fun loadAllFiles() {
        scope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            attachmentRepository.allAttachments()
                .onSuccess { attachments ->
                    _uiState.update { it.copy(isLoading = false, attachments = attachments) }
                }
                .onFailure { error ->
                    _uiState.update { it.copy(isLoading = false, errorMessage = error.havnFriendlyMessage("Could not load files. Please try again.")) }
                }
            refreshUsage()
        }
    }

    fun uploadForProperty(propertyId: String, agencyId: String?, file: HavnPickedFile?) {
        upload(AttachmentUploadContext(propertyId = propertyId, agencyId = agencyId), file)
    }

    fun uploadGeneratedForProperty(
        propertyId: String,
        agencyId: String?,
        file: HavnPickedFile,
        onUploaded: (HavnAttachment) -> Unit,
        onFailed: (String) -> Unit
    ) {
        val currentAgentId = agentId ?: return onFailed("You must be signed in before uploading a file.")
        scope.launch {
            _uiState.update { it.copy(isUploading = true, errorMessage = null) }
            attachmentRepository.uploadAttachment(
                currentAgentId,
                file,
                AttachmentUploadContext(propertyId = propertyId, agencyId = agencyId)
            ).onSuccess { attachment ->
                _uiState.update { state ->
                    state.copy(
                        isUploading = false,
                        attachments = listOf(attachment) + state.attachments.filterNot { it.id == attachment.id }
                    )
                }
                refreshUsage()
                onUploaded(attachment)
            }.onFailure { error ->
                val message = error.havnFriendlyMessage("Could not upload generated PDF.")
                _uiState.update { it.copy(isUploading = false, errorMessage = message) }
                onFailed(message)
            }
        }
    }

    fun uploadGeneratedGlobal(
        agencyId: String?,
        file: HavnPickedFile,
        onUploaded: (HavnAttachment) -> Unit,
        onFailed: (String) -> Unit
    ) {
        val currentAgentId = agentId ?: return onFailed("You must be signed in before uploading a file.")
        scope.launch {
            _uiState.update { it.copy(isUploading = true, errorMessage = null) }
            attachmentRepository.uploadAttachment(
                currentAgentId,
                file,
                AttachmentUploadContext(agencyId = agencyId ?: currentAgentId)
            ).onSuccess { attachment ->
                _uiState.update { state ->
                    state.copy(
                        isUploading = false,
                        attachments = listOf(attachment) + state.attachments.filterNot { it.id == attachment.id }
                    )
                }
                refreshUsage()
                onUploaded(attachment)
            }.onFailure { error ->
                val message = error.havnFriendlyMessage("Could not upload generated PDF.")
                _uiState.update { it.copy(isUploading = false, errorMessage = message) }
                onFailed(message)
            }
        }
    }

    fun uploadForTask(taskId: String, propertyId: String?, agencyId: String?, file: HavnPickedFile?) {
        upload(AttachmentUploadContext(propertyId = propertyId, taskId = taskId, agencyId = agencyId), file)
    }

    fun deleteAttachment(attachment: HavnAttachment) {
        val currentAgentId = agentId ?: return
        if (attachment.uploadedByAgentId != currentAgentId) {
            _uiState.update { it.copy(errorMessage = "Only the file owner can delete this attachment.") }
            return
        }
        scope.launch {
            _uiState.update { it.copy(isUploading = true, errorMessage = null) }
            attachmentRepository.deleteAttachment(currentAgentId, attachment)
                .onSuccess {
                    _uiState.update { state ->
                        state.copy(
                            isUploading = false,
                            attachments = state.attachments.filterNot { it.id == attachment.id }
                        )
                    }
                    refreshUsage()
                }
                .onFailure { error ->
                    _uiState.update { it.copy(isUploading = false, errorMessage = error.havnFriendlyMessage("Could not delete this file. Please try again.")) }
                }
        }
    }

    fun openAttachment(attachment: HavnAttachment) {
        scope.launch {
            _uiState.update { it.copy(errorMessage = null) }
            attachmentRepository.openUrlForAttachment(attachment)
                .onSuccess { url ->
                    if (!HavnExternalActions.openUrl(url)) {
                        _uiState.update { it.copy(errorMessage = "Could not open this file.") }
                    }
                }
                .onFailure { error ->
                    _uiState.update { it.copy(errorMessage = error.havnFriendlyMessage("Could not open this file.")) }
                }
        }
    }

    fun openSelected(attachments: List<HavnAttachment>, photos: List<HavnAttachmentSharePhoto> = emptyList()) {
        if (attachments.isEmpty() && photos.isEmpty()) return
        scope.launch {
            _uiState.update { it.copy(errorMessage = null) }
            val urls = attachmentRepository.shareUrlsForAttachments(attachments)
                .map { signed -> signed.map { it.second } + photos.map { it.url } }
                .getOrElse { error ->
                    _uiState.update { it.copy(errorMessage = error.havnFriendlyMessage("Could not open this file.")) }
                    return@launch
                }
            urls.forEach { url ->
                if (!HavnExternalActions.openUrl(url)) {
                    _uiState.update { it.copy(errorMessage = "Could not open this file.") }
                    return@launch
                }
            }
        }
    }

    fun sendSelectedEmail(recipients: List<HavnAttachmentRecipient>, attachments: List<HavnAttachment>, photos: List<HavnAttachmentSharePhoto> = emptyList()) {
        val emails = recipients.mapNotNull { it.email?.takeIf(String::isNotBlank) }.distinct()
        if (emails.isEmpty()) {
            _uiState.update { it.copy(errorMessage = "This contact does not have an email address.") }
            return
        }
        scope.launch {
            _uiState.update { it.copy(errorMessage = null) }
            if (havnIsWebShell) {
                val currentAgentId = agentId
                if (currentAgentId == null) {
                    _uiState.update { it.copy(errorMessage = "Sign in again before sharing files.") }
                    return@launch
                }
                val body = shortShareBody(currentAgentId, attachments, photos).getOrElse { error ->
                    _uiState.update { it.copy(errorMessage = error.havnFriendlyMessage("Could not prepare file links.")) }
                    return@launch
                }
                if (!HavnExternalActions.openEmailWithAttachments(emails, "HAVN file links", body, emptyList())) {
                    _uiState.update { it.copy(errorMessage = "Unable to open the email app.") }
                } else {
                    recordRecents(recipients)
                }
                return@launch
            }
            val body = emailBody(attachments, photos)
            val emailAttachments = emailAttachments(attachments, photos).getOrElse { error ->
                _uiState.update { it.copy(errorMessage = error.havnFriendlyMessage("Could not prepare selected files.")) }
                return@launch
            }
            if (!HavnExternalActions.openEmailWithAttachments(emails, "HAVN files", body, emailAttachments)) {
                _uiState.update { it.copy(errorMessage = "Unable to open the email app.") }
            } else {
                recordRecents(recipients)
            }
        }
    }

    fun sendSelectedSms(recipients: List<HavnAttachmentRecipient>, attachments: List<HavnAttachment>, photos: List<HavnAttachmentSharePhoto> = emptyList()) {
        val phones = recipients.mapNotNull { it.phone?.takeIf(String::isNotBlank) }.distinct()
        if (phones.isEmpty()) {
            _uiState.update { it.copy(errorMessage = "This contact does not have a phone number.") }
            return
        }
        scope.launch {
            _uiState.update { it.copy(errorMessage = null) }
            val body = shareBody(attachments, photos).getOrElse { error ->
                _uiState.update { it.copy(errorMessage = error.havnFriendlyMessage("Could not prepare selected files.")) }
                return@launch
            }
            if (!HavnExternalActions.openSms(phones.joinToString(","), body)) {
                _uiState.update { it.copy(errorMessage = "Unable to open the messages app.") }
            } else {
                recordRecents(recipients)
            }
        }
    }

    fun recordShareRecipients(recipients: List<HavnAttachmentRecipient>) {
        recordRecents(recipients)
    }

    fun clearError() {
        _uiState.update { it.copy(errorMessage = null) }
    }

    fun dispose() {
        scope.cancel()
    }

    private fun load(propertyId: String?, taskId: String?) {
        scope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            attachmentRepository.attachmentsForContext(propertyId = propertyId, taskId = taskId)
                .onSuccess { attachments ->
                    _uiState.update { it.copy(isLoading = false, attachments = attachments) }
                }
                .onFailure { error ->
                    _uiState.update { it.copy(isLoading = false, errorMessage = error.havnFriendlyMessage("Could not load files. Please try again.")) }
                }
            refreshUsage()
        }
    }

    private fun upload(context: AttachmentUploadContext, file: HavnPickedFile?) {
        if (file == null) return
        val currentAgentId = agentId ?: return
        scope.launch {
            _uiState.update { it.copy(isUploading = true, errorMessage = null) }
            attachmentRepository.uploadAttachment(currentAgentId, file, context)
                .onSuccess { attachment ->
                    _uiState.update { state ->
                        state.copy(
                            isUploading = false,
                            attachments = listOf(attachment) + state.attachments.filterNot { it.id == attachment.id }
                        )
                    }
                    refreshUsage()
                }
                .onFailure { error ->
                    _uiState.update { it.copy(isUploading = false, errorMessage = error.havnFriendlyMessage("Could not upload this file. Please try again.")) }
                }
        }
    }

    private fun refreshUsage() {
        scope.launch {
            attachmentRepository.storageUsage()
                .onSuccess { usage -> _uiState.update { it.copy(storageUsage = usage) } }
        }
    }

    private fun refreshRecents() {
        val currentAgentId = agentId ?: return
        scope.launch {
            attachmentRepository.recentShareRecipients(currentAgentId)
                .onSuccess { recents -> _uiState.update { it.copy(recentRecipients = recents) } }
        }
    }

    private fun recordRecents(recipients: List<HavnAttachmentRecipient>) {
        val currentAgentId = agentId ?: return
        scope.launch {
            recipients.forEach { recipient ->
                attachmentRepository.recordShareRecipient(currentAgentId, recipient)
            }
            refreshRecents()
        }
    }

    private suspend fun shareBody(
        attachments: List<HavnAttachment>,
        photos: List<HavnAttachmentSharePhoto>
    ): Result<String> {
        return attachmentRepository.shareUrlsForAttachments(attachments).map { signedAttachments ->
            buildString {
                append("Hi,\n\n")
                append("Here are the selected HAVN files:\n")
                signedAttachments.forEachIndexed { index, (attachment, url) ->
                    append("\n")
                    append(index + 1)
                    append(". ")
                    append(attachment.fileName)
                    append(" - ")
                    append(url)
                }
                photos.forEachIndexed { index, photo ->
                    append("\n")
                    append(signedAttachments.size + index + 1)
                    append(". ")
                    append(photo.title)
                    append(" - ")
                    append(photo.url)
                }
                append("\n\nAttachment links expire in 7 days.")
            }
        }
    }

    private suspend fun shortShareBody(
        agentId: String,
        attachments: List<HavnAttachment>,
        photos: List<HavnAttachmentSharePhoto>
    ): Result<String> {
        return attachmentRepository.shareUrlsForAttachments(attachments).mapCatching { signedAttachments ->
            val lines = signedAttachments.map { (attachment, url) ->
                HavnAttachmentShareLine(attachment.fileName, url, canShorten = true)
            } + photos.map { photo ->
                HavnAttachmentShareLine(
                    title = photo.title,
                    url = photo.url,
                    canShorten = photo.url.canUseHavnShortShareLink()
                )
            }
            val shortTargets = lines
                .filter { it.canShorten }
                .map { HavnAttachmentShareLinkTarget(it.title, it.url) }
            val shortLinks = attachmentRepository.shortShareUrlsForTargets(agentId, shortTargets)
                .getOrThrow()
                .map { it.second }
                .iterator()
            buildString {
                append("Hi,\n\n")
                append("Here are the selected HAVN files:\n")
                lines.forEachIndexed { index, line ->
                    append("\n")
                    append(index + 1)
                    append(". ")
                    append(line.title)
                    append("\n")
                    append(if (line.canShorten) shortLinks.next() else line.url)
                }
                append("\n\nLinks expire in 7 days.")
            }
        }
    }

    private fun emailBody(
        attachments: List<HavnAttachment>,
        photos: List<HavnAttachmentSharePhoto>
    ): String = buildString {
        append("Hi,\n\n")
        append("I attached the selected HAVN files")
        if (photos.isNotEmpty()) append(" and property photos")
        append(".")
        if (attachments.isNotEmpty() || photos.isNotEmpty()) {
            append("\n\nIncluded:")
            attachments.forEachIndexed { index, attachment ->
                append("\n")
                append(index + 1)
                append(". ")
                append(attachment.fileName)
            }
            photos.forEachIndexed { index, photo ->
                append("\n")
                append(attachments.size + index + 1)
                append(". ")
                append(photo.title)
            }
        }
        append("\n\nBest,")
    }

    private suspend fun emailAttachments(
        attachments: List<HavnAttachment>,
        photos: List<HavnAttachmentSharePhoto>
    ): Result<List<HavnEmailAttachment>> = runCatching {
        val files = attachments.map { attachment ->
            val bytes = attachmentRepository.downloadAttachment(attachment).getOrThrow()
            HavnEmailAttachment(
                fileName = attachment.fileName,
                contentType = attachment.contentType ?: "application/octet-stream",
                bytes = bytes
            )
        }
        val photoFiles = photos.mapIndexedNotNull { index, photo ->
            val dataUrl = photo.thumbnailDataUrl?.takeIf { it.startsWith("data:", ignoreCase = true) }
            val bytes = dataUrl?.decodeHavnDataUrlBytes()?.takeIf { it.isNotEmpty() }
                ?: attachmentRepository.downloadSharePhoto(photo).getOrNull()?.takeIf { it.isNotEmpty() }
                ?: return@mapIndexedNotNull null
            val contentType = dataUrl?.havnDataUrlContentType()
                ?: photo.contentType?.takeIf { it.isNotBlank() }
                ?: "image/jpeg"
            HavnEmailAttachment(
                fileName = "havn-property-photo-${index + 1}.${contentType.photoExtension()}",
                contentType = contentType,
                bytes = bytes
            )
        }
        files + photoFiles
    }
}

private fun String.photoExtension(): String = when {
    contains("png", ignoreCase = true) -> "png"
    contains("webp", ignoreCase = true) -> "webp"
    contains("avif", ignoreCase = true) -> "avif"
    else -> "jpg"
}

private data class HavnAttachmentShareLine(
    val title: String,
    val url: String,
    val canShorten: Boolean
)

private fun String.canUseHavnShortShareLink(): Boolean =
    startsWith("https://", ignoreCase = true) &&
        contains("/storage/v1/object/", ignoreCase = true)

data class AttachmentsUiState(
    val attachments: List<HavnAttachment> = emptyList(),
    val storageUsage: AgentStorageUsage = AgentStorageUsage(),
    val recentRecipients: List<HavnAttachmentRecipient> = emptyList(),
    val currentAgentId: String? = null,
    val isLoading: Boolean = false,
    val isUploading: Boolean = false,
    val errorMessage: String? = null
)
